import toNumber from 'lodash/toNumber'
import isEmpty from 'lodash/isEmpty'
import { orderConfirmQuery } from '../graphql'
import { useError, useMutation, useShippingAddressValidation, useUser } from '@ztechapps/common.hook'
import { usePayNow } from './usePayNow'
import { buildGraphqlQuery, toDate, validatePhoneNumber } from '@ztechapps/common.utils'
import { OrderCheckoutActivePanelEnum } from '@ztechapps/order.common'
import { useValidateShipment } from './useValidateShipment'
import { useUpdateOrder } from './useUpdateOrder'
import type { MutationResult } from '@apollo/client'
import { useCheckoutContext } from '../index'

const customerQueries = buildGraphqlQuery('Customer', '_id')

type ActionResult = [(args?: any) => Promise<void>, MutationResult]

export const useConfirmOrderAction = (): ActionResult => {
  const context = useCheckoutContext()
  const [, setShowLoadingOverlay] = context.showLoadingOverlay

  const [message] = context.message
  const _onSuccess = context.onSuccess
  const { document } = context.orderDocumentResult

  const onSuccess = async () => {
    await _onSuccess?.()
  }

  const orderId = document?._id
  const [confirmOrder, , mutationResult] = useMutation({
    query: orderConfirmQuery,
  })
  const { onError } = useError()

  const onConfirmOrder = async ({ onClick }) => {
    try {
      setShowLoadingOverlay(true)

      // //validate of the input
      // if (isEmpty( message)) {
      //   throw new Error('Please enter some message for the us!')
      // }

      //we will send the confirm order and it's initial notification
      await confirmOrder({
        input: {
          order: {
            _id: orderId,
          },
          message: isEmpty(message) ? 'Please review my order!' : message,
        },
      })

      if (onClick) {
        onClick?.()
      } else {
        await onSuccess()
      }
    } catch (e) {
      onError(e)
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return [onConfirmOrder, mutationResult]
}

export const useBackAction = (): ActionResult => {
  const context = useCheckoutContext()
  const [activePanel, setActivePanel] = context.activePanel

  const onBack = async () => {
    if (activePanel <= OrderCheckoutActivePanelEnum.item) return
    setActivePanel(activePanel - 1)
  }

  return [onBack, null]
}

export const useItemAction = (): ActionResult => {
  const context = useCheckoutContext()
  const [, setActivePanel] = context.activePanel

  const onClickItem = async () => {
    setActivePanel(OrderCheckoutActivePanelEnum.item)
  }

  return [onClickItem, null]
}

export const useShippingOptionsAction = (): ActionResult => {
  const context = useCheckoutContext()
  const [, setActivePanel] = context.activePanel

  const onClickItem = async () => {
    setActivePanel(OrderCheckoutActivePanelEnum.shippingOptions)
  }

  return [onClickItem, null]
}

export const useShippingRateAction = (): ActionResult => {
  const [updateUser, , mutationResult] = useMutation(customerQueries.updateConfig)
  const [updateOrder, { error: updateOrderError, loading: updateOrderLoading }] = useUpdateOrder()
  const { onError } = useError()

  const addressValidation = useShippingAddressValidation()
  const context = useCheckoutContext()
  const [, setShowLoadingOverlay] = context.showLoadingOverlay
  const [, setActivePanel] = context.activePanel

  //const { setActivePanel, setShowLoadingOverlay, shippingOptions } = useContext()
  const [shippingOptions] = context.shippingOptions
  const { document } = context.orderDocumentResult

  const customer = document?.by
  const { user } = useUser()

  const validate = async () => {
    //validate the shipping date
    if (!shippingOptions?.shipmentDate) {
      throw new Error('Please set "Shipping date"')
    } else {
      const shipmentDate = toDate(shippingOptions?.shipmentDate, user?.timeZone)
      if (shipmentDate.isBefore(toDate(undefined, user?.timeZone), 'day')) {
        throw new Error('Shipping date cannot be in the past.')
      }
    }

    if (!shippingOptions?.shippingAddress?.street1) throw new Error('Please set the shipping address (Address Line 1)')
    if (!shippingOptions?.shippingAddress?.city) throw new Error('Please set the shipping address (City/District)')
    if (!shippingOptions?.shippingAddress?.state)
      throw new Error('Please set the shipping address (State/Province/Region)')
    if (!shippingOptions?.shippingAddress?.zip) throw new Error('Please set the shipping address (ZIP/Postal Code)')
    if (!shippingOptions?.shippingAddress?.country) throw new Error('Please set the shipping address (Country)')
    if (!isEmpty(shippingOptions.shippingAddress.errors)) throw new Error('Please set a valid shipping address.')

    // if (shippingOptions?.shippingAddress?.country === 'USA' && shippingOptions?.shippingAddress?.state?.length !== 2) {
    //   throw new Error('The State/Province/Region should be 2 characters only, use the state and territory postal abbreviations ')
    // }

    //ship_to state_province must be two characters when ship_to country_code equals US1

    //validate street1
    addressValidation?.validate({ address: shippingOptions?.shippingAddress?.street1 })

    if (!shippingOptions?.name) throw new Error('Please set "Your Name" as the recipient for this shipment.')

    if (!shippingOptions?.companyName) throw new Error('Please set "Company Name" as the recipient for this shipment.')

    if (isEmpty(shippingOptions?.phone))
      throw new Error('Phone number is required so we can easily contact you if there are issues with the shipment.')
    else {
      validatePhoneNumber(shippingOptions?.phone)
    }

    if (shippingOptions.insuredAmount < 100) {
      throw new Error('Insured amount cannot be less than 100')
    }

    return true
  }

  const onClickShippingRate = async () => {
    try {
      setShowLoadingOverlay(true)
      await validate()
      const insuredAmount = toNumber(shippingOptions?.insuredAmount)

      const shipmentDate = toDate(shippingOptions?.shipmentDate)?.toDate() ?? null

      const input = {
        _id: document?._id,
        shippingAddress: { ...shippingOptions?.shippingAddress, phone: shippingOptions?.phone },
        //'billingAddress.company': document?.billingAddress?.company ? document?.billingAddress?.company : shippingOptions?.shippingAddress?.company,
        customerPoNumber: shippingOptions?.customerPoNumber,
        'shipping.date': shipmentDate,
        'shipping.deliveryConfirmation': shippingOptions?.deliveryConfirmation,
        'shipping.insuredAmount': insuredAmount,
      }

      await updateOrder({ input })

      //update the customer info
      let updateCustomer = false
      const customerInput: { input: any } = { input: { _id: customer?._id } }
      if (isEmpty(customer?.address?.fullAddress) || isEmpty(customer?.address?.street1)) {
        updateCustomer = true
        //generate the fullAddress without the company
        let fullAddress = ''
        if (shippingOptions?.shippingAddress?.company) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.company}`
            : `${shippingOptions?.shippingAddress?.company}`
        }

        if (shippingOptions?.shippingAddress?.street1) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.street1}`
            : `${shippingOptions?.shippingAddress?.street1}`
        }
        if (shippingOptions?.shippingAddress?.street2) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.street2}`
            : `${shippingOptions?.shippingAddress?.street2}`
        }
        if (shippingOptions?.shippingAddress?.street3)
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.street3}`
            : `${shippingOptions?.shippingAddress?.street3}`

        if (shippingOptions?.shippingAddress?.city) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.city}`
            : `${shippingOptions?.shippingAddress?.city}`
        }
        if (shippingOptions?.shippingAddress?.state) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.state}`
            : `${shippingOptions?.shippingAddress?.state}`
        }
        if (shippingOptions?.shippingAddress?.country) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.country}`
            : `${shippingOptions?.shippingAddress?.country}`
        }
        if (shippingOptions?.shippingAddress?.zip) {
          fullAddress = fullAddress
            ? `${fullAddress}, ${shippingOptions?.shippingAddress?.zip}`
            : `${shippingOptions?.shippingAddress?.zip}`
        }

        if (!isEmpty(shippingOptions?.shippingAddress?.company)) {
          //need to have one entry on addresses that contain the company name
          customerInput.input.addresses = [{ ...shippingOptions?.shippingAddress, fullAddress }]
        }

        customerInput.input.address = { ...shippingOptions?.shippingAddress, fullAddress }
        //company address does not have company name
        //remove the shipping address company≈
        delete customerInput.input.address.company
      }

      //if the phones are not the same update it
      if (customer?.phone !== shippingOptions?.phone) {
        updateCustomer = true
        customerInput.input.phone = shippingOptions?.phone
      }

      //a loaded user who is not the main contact cannot write the main contact's record
      const isCollaborator = user && user._id !== customer?._id
      if (updateCustomer && !isCollaborator) {
        await updateUser(customerInput)
      }

      //move to the shipping rates
      setActivePanel(OrderCheckoutActivePanelEnum.shippingRate)
    } catch (e) {
      onError(e)
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return [
    onClickShippingRate,
    {
      ...mutationResult,
      error: updateOrderError || mutationResult.error,
      loading: updateOrderLoading || mutationResult.loading,
    },
  ]
}

export const usePaymentMethodAction = (): ActionResult => {
  const context = useCheckoutContext()
  const [shippingOptions] = context.shippingOptions
  const billTo = shippingOptions.shippingBillTo
  const { document } = context.orderDocumentResult
  const [, setShowLoadingOverlay] = context.showLoadingOverlay
  const [, setActivePanel] = context.activePanel

  const [updateOrder, mutationResult] = useUpdateOrder()
  const validateShipment = useValidateShipment()
  const payNow = usePayNow()
  const { onError } = useError()

  const onClickPaymentMethod = async () => {
    try {
      setShowLoadingOverlay(true)

      //if bill to me and the billToAccount, etc are not specified then ask for it
      if (billTo?.isBillToCustomer) {
        if (!billTo?.account) throw new Error('Please set the shipping carrier account #.')
        if (!billTo?.countryCode) throw new Error('Please set the shipping carrier country code.')
        if (!billTo?.postalCode) throw new Error('Please set the shipping carrier postal code.')
      }

      //always update the billToAccount on order
      await updateOrder({
        input: {
          _id: document?._id,
          'shipping.billToCustomer': billTo?.isBillToCustomer,
          'shipping.billToAccount': billTo?.account,
          'shipping.billToPostalCode': billTo?.postalCode,
          'shipping.billToCountryCode': billTo?.countryCode,
        },
      })

      await payNow()

      await validateShipment.validate()

      setActivePanel(OrderCheckoutActivePanelEnum.paymentMethod)
    } catch (e) {
      onError(e)
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return [onClickPaymentMethod, mutationResult]
}
